import { useAuthStore } from '@/stores/authStore';

/**
 * API Base URL（クライアントサイド用）
 * - 相対パスを使用してNext.js API Routesを経由
 * - これによりSSL/CORSの問題を回避
 */
export const API_BASE_URL = '/api/v1';

/**
 * 認証ヘッダーを取得
 */
export function getAuthHeaders(): HeadersInit {
  const accessToken = useAuthStore.getState().accessToken;
  
  if (accessToken) {
    return {
      'Authorization': `Bearer ${accessToken}`,
    };
  }
  
  return {};
}

/**
 * タイムアウト時間（ミリ秒）
 */
const DEFAULT_TIMEOUT_MS = 10000; // 10秒（通常のリクエスト）
const LONG_TIMEOUT_MS = 60000;    // 1分（画像生成・投稿などの重い処理）

/**
 * タイムアウト時間を取得
 * 特定のエンドポイントには長いタイムアウトを適用
 */
function getTimeout(url: string): number {
  // アイキャッチ生成、Pontanuki投稿、記事生成などは長いタイムアウト
  const longTimeoutPatterns = [
    '/eyecatch/',
    '/pontanuki/',
    '/article-ideas/',
    '/agent-workflow/',
    '/publications/article/',
  ];

  if (longTimeoutPatterns.some(pattern => url.includes(pattern))) {
    return LONG_TIMEOUT_MS;
  }

  return DEFAULT_TIMEOUT_MS;
}

/**
 * 認証付きfetch（タイムアウト対応）
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const authHeaders = getAuthHeaders();
  const headers = {
    ...options.headers,
    ...authHeaders,
  };

  // AbortControllerを作成してタイムアウト処理を追加
  const controller = new AbortController();
  const timeout = getTimeout(url);
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // 401エラー時はトークンリフレッシュを試みる
    if (response.status === 401) {
      try {
        await useAuthStore.getState().refreshAccessToken();

        // リトライ
        const retryAuthHeaders = getAuthHeaders();
        const retryHeaders = {
          ...options.headers,
          ...retryAuthHeaders,
        };

        return fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      } catch (error) {
        // リフレッシュ失敗時はログアウト
        useAuthStore.getState().logout();
        throw error;
      }
    }

    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);

    // タイムアウトエラーの場合
    if (error.name === 'AbortError') {
      throw new Error('リクエストがタイムアウトしました');
    }

    throw error;
  }
}